/**
 * 대화 열(더 묻기)과 사전 점검 카드를 잇는 얇은 계층 — 2026-09-14 사용자 결정(대화 열을 카드 밖 결과 왼쪽에 접이식으로).
 *
 * 카드가 계산한 문맥(답한 사실·살아 있는 후보와 명칭·후보 호·채울 수 있는 질문 키·좁혀진 세번)을 검색어 단위로
 * 게시하고, 대화 열이 구독한다. 대화 열이 답 속 세번을 누르면 `requestAskFlash`로 카드의 그 마디를 켠다.
 * 채우기 칩은 여기 없다 — 대화 열이 `treeFactStore.saveTreeFacts`에 직접 쓰고 카드가 그 이벤트로 재계산한다.
 * 저장 상태는 열림/닫힘(localStorage) 하나뿐이다. 문맥은 메모리에만 있다(카드가 살아 있을 때만 뜻이 있다).
 */
import { useEffect, useState } from "react";

export const ASK_CONTEXT_EVENT = "loginhs:ask-context";
export const ASK_FLASH_EVENT = "loginhs:ask-flash";
export const ASK_FIND_EVENT = "loginhs:ask-find";
export const ASK_DRAWER_OPEN_KEY = "loginhs:ask-drawer";
export const ASK_DRAWER_WIDTH_KEY = "loginhs:ask-drawer-width";
export const DRAWER_MIN_WIDTH = 280;
export const DRAWER_MAX_WIDTH = 640;
export const DRAWER_DEFAULT_WIDTH = 380;

const normalizeQuery = (q) => String(q || "").trim().replace(/\s+/g, " ");
let current = null;

/** 카드가 문맥을 게시한다. 같은 검색어의 이전 문맥은 덮어쓴다. */
export function publishAskContext(ctx) {
  if (!ctx || !normalizeQuery(ctx.q)) return;
  current = { ...ctx, q: normalizeQuery(ctx.q) };
  try { window.dispatchEvent(new CustomEvent(ASK_CONTEXT_EVENT, { detail: current })); } catch (_) { /* ignore */ }
}

/** 카드가 사라질 때(검색어 바뀜·트리 없음) 그 검색어의 문맥만 지운다. */
export function clearAskContext(q) {
  if (!current || current.q !== normalizeQuery(q)) return;
  current = null;
  try { window.dispatchEvent(new CustomEvent(ASK_CONTEXT_EVENT, { detail: null })); } catch (_) { /* ignore */ }
}

export function peekAskContext(q) {
  return current && current.q === normalizeQuery(q) ? current : null;
}

/** 대화 열이 구독한다. 검색어가 다르면 null(문맥 없이 검색어만으로 묻는다). */
export function useAskContext(q) {
  const key = normalizeQuery(q);
  const [ctx, setCtx] = useState(() => peekAskContext(key));
  useEffect(() => {
    setCtx(peekAskContext(key));
    const onEvent = (e) => { const c = e.detail; setCtx(c && c.q === key ? c : (current && current.q === key ? current : null)); };
    window.addEventListener(ASK_CONTEXT_EVENT, onEvent);
    return () => window.removeEventListener(ASK_CONTEXT_EVENT, onEvent);
  }, [key]);
  return ctx;
}

/** 답 속 세번 → 카드의 그 마디 선택. */
export function requestAskFlash(q, code) {
  try { window.dispatchEvent(new CustomEvent(ASK_FLASH_EVENT, { detail: { q: normalizeQuery(q), code: String(code || "").replace(/\D/g, "") } })); } catch (_) { /* ignore */ }
}

/** 대화 열의 "X 찾아줘" → 카드 지도의 찾기 칸. 이름이 맞는 마디를 노랗게 표시한다(LLM 호출 없음). */
export function requestAskFind(q, term) {
  try { window.dispatchEvent(new CustomEvent(ASK_FIND_EVENT, { detail: { q: normalizeQuery(q), term: String(term || "").trim() } })); } catch (_) { /* ignore */ }
}

export function useAskFind(q, handler) {
  const key = normalizeQuery(q);
  useEffect(() => {
    const onEvent = (e) => { const d = e.detail; if (d && d.q === key && handler) handler(d.term || ""); };
    window.addEventListener(ASK_FIND_EVENT, onEvent);
    return () => window.removeEventListener(ASK_FIND_EVENT, onEvent);
  }, [key, handler]);
}

/** 카드가 구독한다. handler(code)는 그 검색어의 요청에만 불린다. */
export function useAskFlash(q, handler) {
  const key = normalizeQuery(q);
  useEffect(() => {
    const onEvent = (e) => { const d = e.detail; if (d && d.q === key && d.code && handler) handler(d.code); };
    window.addEventListener(ASK_FLASH_EVENT, onEvent);
    return () => window.removeEventListener(ASK_FLASH_EVENT, onEvent);
  }, [key, handler]);
}

export function readDrawerOpen() {
  try { return localStorage.getItem(ASK_DRAWER_OPEN_KEY) !== "0"; } catch (_) { return true; }
}

export function writeDrawerOpen(open) {
  try { localStorage.setItem(ASK_DRAWER_OPEN_KEY, open ? "1" : "0"); } catch (_) { /* ignore */ }
}

export function clampDrawerWidth(w) {
  const n = Number(w);
  if (!Number.isFinite(n)) return DRAWER_DEFAULT_WIDTH;
  return Math.min(DRAWER_MAX_WIDTH, Math.max(DRAWER_MIN_WIDTH, Math.round(n)));
}

/** 사용자가 손잡이로 정한 대화 열 폭. 없으면 기본 380. */
export function readDrawerWidth() {
  try { const raw = localStorage.getItem(ASK_DRAWER_WIDTH_KEY); return raw ? clampDrawerWidth(raw) : DRAWER_DEFAULT_WIDTH; } catch (_) { return DRAWER_DEFAULT_WIDTH; }
}

export function writeDrawerWidth(w) {
  try { localStorage.setItem(ASK_DRAWER_WIDTH_KEY, String(clampDrawerWidth(w))); } catch (_) { /* ignore */ }
}

/** 시험용. */
export function resetAskContext() { current = null; }
